import { ZCDBarChartDataset } from '@zencity/common-ui/lib/ZCD/ZCDCharts/types';
import {
  concernsColorsOrder,
  extractSortedTopConcerns,
  filterOutMissingDivision,
  sortByPolygonName,
  sortByTotalClassifications,
  VaultAnswerClassificationSubTopicBreakdownResponse,
} from 'screens/WidgetGenerator/utils/answerClassificationBreakdown';
import { ColorByNumericId } from 'screens/WidgetGenerator/reportGeneratorComponents/types';
import { abbreviatePolygonNames, overExtendedPolygonNameExist } from 'screens/WidgetGenerator/helpers/polygonNames';

const DEFAULT_MAX_TOPICS_ITEMS = 5;
const DEFAULT_MAX_DIVISIONS_ITEMS = 13;
const TOO_MANY_DIVISIONS_THRESHOLD = 6;
const TOO_MANY_DIVISIONS_MAX_TOPICS_ITEMS = 3;
const POLYGON_NAME_MAX_LENGTH = 12;

interface DivisionForBreakdown {
  [divisionId: string]: {
    divisionId: number;
    polygonName: string;
    percentageToDisplay: number;
  };
}
interface BreakdownByTopicsByArea {
  [topicId: string]: {
    topicId: number;
    topicLabel: string;
    divisions: DivisionForBreakdown;
  };
}

const preparePolygonsDataPerTopic = (topicsData: {
  topicId: number;
  topicLabel: string;
  divisions: DivisionForBreakdown;
}) => {
  const divisionIdsToPolygonNames = Object.fromEntries(
    Object.values(topicsData.divisions).map((divisionData) => [divisionData.divisionId, divisionData.polygonName]),
  );
  // Should we encounter a name that exceeds the required length, we are probably not inclined to modify the entire set
  // of names. However, it is crucial to inspect all names to verify that their uniqueness is preserved.
  const divisionIdsToAbbreviatePolygonNames = overExtendedPolygonNameExist({
    polygonNames: Object.values(divisionIdsToPolygonNames),
    maxCharactersLimit: POLYGON_NAME_MAX_LENGTH,
  })
    ? abbreviatePolygonNames({
        divisionIdsToPolygonNames,
        maxCharactersLimit: POLYGON_NAME_MAX_LENGTH,
      })
    : divisionIdsToPolygonNames;

  return Object.values(topicsData.divisions).map((divisionData) => ({
    label: divisionIdsToAbbreviatePolygonNames[divisionData.divisionId],
    value: divisionData.percentageToDisplay,
  }));
};

export const prepareDataset = (
  rawData: VaultAnswerClassificationSubTopicBreakdownResponse,
  overrideColorMap?: ColorByNumericId,
): ZCDBarChartDataset[] => {
  let maxTopics = DEFAULT_MAX_TOPICS_ITEMS;
  const divisionsCount = Object.values(rawData.divisions).filter(filterOutMissingDivision).length;
  if (divisionsCount > TOO_MANY_DIVISIONS_THRESHOLD) {
    maxTopics = TOO_MANY_DIVISIONS_MAX_TOPICS_ITEMS;
  }

  // The datasets of this widget should use the same order and colors of the
  // `ClassificationTopConcernsHOC` widget, thus, using the same function to
  // create it (`extractSortedTopConcerns`).
  const sortedTopConcerns = extractSortedTopConcerns(rawData);
  const concernsOrder = sortedTopConcerns.map<number>((item) => item.id);
  const colorByTopicId = sortedTopConcerns.reduce<ColorByNumericId>(
    (accum, item, index) => ({
      ...accum,
      [item.id]: overrideColorMap?.[item.id] ?? concernsColorsOrder[index],
    }),
    {},
  );

  const relevantData = Object.values(rawData.divisions)
    .filter(filterOutMissingDivision)
    .slice(0, DEFAULT_MAX_DIVISIONS_ITEMS)
    .sort(sortByPolygonName)
    .map((divisionData) => {
      const topicsToDisplay = Object.values(divisionData.topics)
        .sort(sortByTotalClassifications)
        .slice(0, maxTopics)
        .map((topicData) => {
          const percentageToDisplay = Math.round(
            (divisionData.total_classifications > 0
              ? topicData.total_classifications / divisionData.total_classifications
              : 1) * 100,
          );

          return {
            ...topicData,
            percentageToDisplay,
          };
        });

      return {
        ...divisionData,
        topics: topicsToDisplay,
      };
    });

  const aggregatedData = {} as BreakdownByTopicsByArea;
  Object.values(relevantData).forEach((divisionData) => {
    Object.values(divisionData.topics).forEach((topicData) => {
      if (!Object.hasOwn(aggregatedData, topicData.topic_id)) {
        aggregatedData[topicData.topic_id] = {
          topicId: topicData.topic_id,
          topicLabel: topicData.topic_label,
          divisions: {},
        };
      }

      if (!Object.hasOwn(aggregatedData[topicData.topic_id].divisions, divisionData.division_id)) {
        aggregatedData[topicData.topic_id].divisions[divisionData.division_id] = {
          divisionId: divisionData.division_id,
          polygonName: divisionData.polygon_name,
          percentageToDisplay: topicData.percentageToDisplay,
        };
      } else {
        throw new Error(`Division ${divisionData.division_id} already exists for topic ${topicData.topic_id}`);
      }
    });
  });

  return Object.values(aggregatedData)
    .sort((topicA, topicB) => concernsOrder.indexOf(topicA.topicId) - concernsOrder.indexOf(topicB.topicId))
    .map(
      (topicsData) =>
        ({
          label: topicsData.topicLabel,
          backgroundColor: colorByTopicId[topicsData.topicId],
          // eslint-disable-next-line id-denylist
          data: preparePolygonsDataPerTopic(topicsData),
        } as ZCDBarChartDataset),
    );
};
